import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import AddIcon from "@mui/icons-material/Add"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import MoveDownIcon from "@mui/icons-material/MoveDown"
import InventoryIcon from "@mui/icons-material/Inventory"
import VisibilityIcon from "@mui/icons-material/Visibility"

import MainLayout from "../../layouts/MainLayout"

import {
  addWarehouseStock,
  createWarehouse,
  deactivateWarehouse,
  getWarehouseFormData,
  getWarehouseStockItems,
  getWarehouses,
  normalizeApiArray,
  transferWarehouseToStore,
  transferWarehouseToWarehouse,
  updateWarehouse,
} from "../../services/warehouseService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const WAREHOUSE_TYPES = [
  {
    value: "FINISHED_GOODS",
    label: "Produits finis",
  },
  {
    value: "RAW_MATERIALS",
    label: "Matières premières",
  },
  {
    value: "MIXED",
    label: "Mixte",
  },
  {
    value: "TRANSIT",
    label: "Transit",
  },
]


const emptyWarehouseForm = {
  code: "",
  name: "",
  warehouse_type: "FINISHED_GOODS",
  address: "Site principal entreprise",
  city: "Kinshasa",
  province: "Kinshasa",
  is_active: true,
}


const emptyStockForm = {
  warehouse_id: "",
  product_id: "",
  quantity: "",
  unit_cost: "",
  notes: "",
}


const emptyTransferStoreForm = {
  source_warehouse_id: "",
  destination_store_id: "",
  product_id: "",
  quantity: "",
  unit_cost: "",
  notes: "",
}


const emptyTransferWarehouseForm = {
  source_warehouse_id: "",
  destination_warehouse_id: "",
  product_id: "",
  quantity: "",
  unit_cost: "",
  notes: "",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatQuantity(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  )
}


function isActive(value) {
  if (
    value === false ||
    value === "false" ||
    value === 0 ||
    value === "0"
  ) {
    return false
  }

  return true
}


function hasNumericValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number(value) > 0
  )
}


function hasValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== ""
  )
}


function formatCurrency(
  value,
  currency = "USD"
) {
  if (!hasValue(value)) {
    return "-"
  }

  const digits =
    currency === "CDF"
      ? 0
      : 2

  return `${toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits:
        digits,

      maximumFractionDigits:
        digits,
    }
  )} ${currency}`
}


function formatUsdCdf(
  usdValue,
  cdfValue
) {
  const usd =
    hasValue(usdValue)
      ? formatCurrency(
          usdValue,
          "USD"
        )
      : "-"

  const cdf =
    hasNumericValue(cdfValue)
      ? formatCurrency(
          cdfValue,
          "CDF"
        )
      : ""

  return cdf
    ? `${usd} / ${cdf}`
    : usd
}


function getWarehouseTypeLabel(
  value
) {
  return (
    WAREHOUSE_TYPES.find(
      (item) =>
        item.value === value
    )?.label ||
    value ||
    "Produits finis"
  )
}


function getWarehouseTypeColor(
  value
) {
  switch (value) {
    case "RAW_MATERIALS":
      return "warning"

    case "FINISHED_GOODS":
      return "success"

    case "MIXED":
      return "info"

    case "TRANSIT":
      return "secondary"

    default:
      return "default"
  }
}


function findProduct(
  products,
  productId
) {
  return (
    products.find(
      (item) =>
        String(item.id) ===
        String(productId)
    ) ||
    null
  )
}


function getWarehouseLabel(
  warehouse
) {
  if (!warehouse) {
    return "-"
  }

  return `${warehouse.code || ""} - ${
    warehouse.name || ""
  } • ${getWarehouseTypeLabel(
    warehouse.warehouse_type
  )}`
}


function getStoreLabel(
  store
) {
  if (!store) {
    return "-"
  }

  const organization =
    store.organization_code
      ? `${store.organization_code} • `
      : ""

  return `${organization}${store.code || ""} - ${
    store.name || ""
  }`
}


function getProductFactoryPrice(
  products,
  productId
) {
  const product =
    findProduct(
      products,
      productId
    )

  if (!product) {
    return ""
  }

  return String(
    product.factory_price ||
      ""
  )
}


function getProductSearchLabel(
  product
) {
  if (!product) {
    return ""
  }

  return `${product.code || ""} ${
    product.name || ""
  } ${product.price_currency || ""}`
}


function renderProductOption(
  props,
  product
) {
  const {
    key,
    ...optionProps
  } = props

  const factoryCdf =
    product.display_factory_price_cdf ||
    product.factory_price_cdf

  const sellingCdf =
    product.display_selling_price_cdf ||
    product.selling_price_cdf

  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
    >
      <Stack
        spacing={0.3}
        sx={{
          width: "100%",
          minWidth: 0,
        }}
      >
        <Typography
          fontWeight={900}
          color="#0f172a"
        >
          {product.code} -{" "}
          {product.name}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
        >
          Achat :{" "}
          {formatUsdCdf(
            product.factory_price,
            factoryCdf
          )}
          {" • "}
          Vente :{" "}
          {formatUsdCdf(
            product.selling_price,
            sellingCdf
          )}
        </Typography>
      </Stack>
    </Box>
  )
}


function getApiErrorMessage(
  error,
  fallback = "Action impossible."
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// PRICE BLOCK
// =========================================================

function PriceBlock({
  usd,
  cdf,
  align = "right",
}) {
  const alignItems =
    align === "left"
      ? "flex-start"
      : "flex-end"

  return (
    <Stack
      spacing={0.15}
      alignItems={alignItems}
    >
      <Typography
        variant="body2"
        fontWeight={900}
        noWrap
      >
        {formatCurrency(
          usd,
          "USD"
        )}
      </Typography>

      {hasNumericValue(cdf) && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
        >
          {formatCurrency(
            cdf,
            "CDF"
          )}
        </Typography>
      )}
    </Stack>
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.2,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.15,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,

              color: "#0f172a",

              fontSize: {
                xs: 18,
                sm: 20,
              },

              lineHeight: 1.1,

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",
            }}
          >
            {value}
          </Typography>


          <Typography
            variant="body2"
            sx={{
              mt: 0.2,

              fontWeight: 800,

              color: "#334155",

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Typography>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.2,

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function WarehousesPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    stockItems,
    setStockItems,
  ] = useState([])


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    selectedWarehouseId,
    setSelectedWarehouseId,
  ] = useState("")

  const [
    search,
    setSearch,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    stockPage,
    setStockPage,
  ] = useState(0)

  const [
    warehousePage,
    setWarehousePage,
  ] = useState(0)


  // =======================================================
  // LOADING
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)


  // =======================================================
  // DIALOGS
  // =======================================================

  const [
    warehouseDialogOpen,
    setWarehouseDialogOpen,
  ] = useState(false)

  const [
    stockDialogOpen,
    setStockDialogOpen,
  ] = useState(false)

  const [
    transferStoreDialogOpen,
    setTransferStoreDialogOpen,
  ] = useState(false)

  const [
    transferWarehouseDialogOpen,
    setTransferWarehouseDialogOpen,
  ] = useState(false)


  // =======================================================
  // FORMS
  // =======================================================

  const [
    editingWarehouse,
    setEditingWarehouse,
  ] = useState(null)

  const [
    warehouseForm,
    setWarehouseForm,
  ] = useState({
    ...emptyWarehouseForm,
  })

  const [
    stockForm,
    setStockForm,
  ] = useState({
    ...emptyStockForm,
  })

  const [
    transferStoreForm,
    setTransferStoreForm,
  ] = useState({
    ...emptyTransferStoreForm,
  })

  const [
    transferWarehouseForm,
    setTransferWarehouseForm,
  ] = useState({
    ...emptyTransferWarehouseForm,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const closeSnackbar = () => {
    setSnackbar(
      (previous) => ({
        ...previous,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD DATA
  // =======================================================

  const loadData =
    async () => {
      try {
        setLoading(true)

        const [
          warehousesResult,
          formDataResult,
          stockItemsResult,
        ] =
          await Promise.allSettled([
            getWarehouses(),
            getWarehouseFormData(),
            getWarehouseStockItems(),
          ])


        let loadedWarehouses = []
        let loadedProducts = []
        let loadedStores = []
        let loadedStockItems = []
        let fallbackWarehouses = []


        if (
          warehousesResult.status ===
          "fulfilled"
        ) {
          loadedWarehouses =
            normalizeApiArray(
              warehousesResult.value,
              "warehouses"
            )
        } else {
          console.error(
            "Erreur chargement entrepôts :",
            warehousesResult.reason
          )

          showSnackbar(
            getApiErrorMessage(
              warehousesResult.reason,
              "Impossible de charger la liste des entrepôts."
            ),
            "error"
          )
        }


        if (
          formDataResult.status ===
          "fulfilled"
        ) {
          loadedProducts =
            normalizeApiArray(
              formDataResult.value
                ?.products,
              "products"
            )

          loadedStores =
            normalizeApiArray(
              formDataResult.value
                ?.stores,
              "stores"
            )

          fallbackWarehouses =
            normalizeApiArray(
              formDataResult.value
                ?.warehouses,
              "warehouses"
            )
        } else {
          console.error(
            "Erreur chargement form-data entrepôts :",
            formDataResult.reason
          )

          showSnackbar(
            getApiErrorMessage(
              formDataResult.reason,
              "Impossible de charger les produits et les shops."
            ),
            "warning"
          )
        }


        if (
          stockItemsResult.status ===
          "fulfilled"
        ) {
          loadedStockItems =
            normalizeApiArray(
              stockItemsResult.value,
              "items"
            )
        } else {
          console.error(
            "Erreur chargement stock-items :",
            stockItemsResult.reason
          )

          showSnackbar(
            getApiErrorMessage(
              stockItemsResult.reason,
              "Les entrepôts sont chargés, mais le stock produit par produit n'a pas pu être chargé."
            ),
            "warning"
          )
        }


        setWarehouses(
          loadedWarehouses.length >
            0
            ? loadedWarehouses
            : fallbackWarehouses
        )

        setProducts(
          loadedProducts
        )

        setStores(
          loadedStores
        )

        setStockItems(
          loadedStockItems
        )
      } finally {
        setLoading(false)
      }
    }


  useEffect(() => {
    loadData()
  }, [])


  // =======================================================
  // ACTIVE WAREHOUSES
  // =======================================================

  const activeWarehouses =
    useMemo(() => {
      return warehouses.filter(
        (warehouse) =>
          isActive(
            warehouse.is_active
          )
      )
    }, [
      warehouses,
    ])


  // =======================================================
  // FILTER STOCK
  // =======================================================

  const visibleStockItems =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return stockItems.filter(
        (item) => {
          const matchWarehouse =
            !selectedWarehouseId ||
            String(
              item.warehouse_id
            ) ===
              String(
                selectedWarehouseId
              )

          const matchSearch =
            !keyword ||
            [
              item.product_code,
              item.product_name,
              item.warehouse_code,
              item.warehouse_name,
              item.warehouse_type,
              getWarehouseTypeLabel(
                item.warehouse_type
              ),
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          return (
            matchWarehouse &&
            matchSearch
          )
        }
      )
    }, [
      stockItems,
      selectedWarehouseId,
      search,
    ])


  // =======================================================
  // FILTER WAREHOUSES
  // =======================================================

  const filteredWarehouses =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return warehouses.filter(
        (warehouse) => {
          const matchesWarehouse =
            !selectedWarehouseId ||
            String(
              warehouse.id
            ) ===
              String(
                selectedWarehouseId
              )

          const matchesSearch =
            !keyword ||
            [
              warehouse.code,
              warehouse.name,
              warehouse.warehouse_type,
              getWarehouseTypeLabel(
                warehouse.warehouse_type
              ),
              warehouse.city,
              warehouse.province,
              warehouse.address,
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          return (
            matchesWarehouse &&
            matchesSearch
          )
        }
      )
    }, [
      warehouses,
      selectedWarehouseId,
      search,
    ])


  // =======================================================
  // RESET PAGES AFTER FILTER
  // =======================================================

  useEffect(() => {
    setStockPage(0)
    setWarehousePage(0)
  }, [
    search,
    selectedWarehouseId,
  ])


  // =======================================================
  // PAGINATION
  // =======================================================

  const paginatedStockItems =
    useMemo(() => {
      const start =
        stockPage *
        ROWS_PER_PAGE

      return visibleStockItems.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      visibleStockItems,
      stockPage,
    ])


  const paginatedWarehouses =
    useMemo(() => {
      const start =
        warehousePage *
        ROWS_PER_PAGE

      return filteredWarehouses.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredWarehouses,
      warehousePage,
    ])


  // =======================================================
  // KEEP STOCK PAGE VALID
  // =======================================================

  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          visibleStockItems.length /
            ROWS_PER_PAGE
        )
      )

    if (
      stockPage >=
      totalPages
    ) {
      setStockPage(
        totalPages - 1
      )
    }
  }, [
    visibleStockItems.length,
    stockPage,
  ])


  // =======================================================
  // KEEP WAREHOUSE PAGE VALID
  // =======================================================

  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredWarehouses.length /
            ROWS_PER_PAGE
        )
      )

    if (
      warehousePage >=
      totalPages
    ) {
      setWarehousePage(
        totalPages - 1
      )
    }
  }, [
    filteredWarehouses.length,
    warehousePage,
  ])


  // =======================================================
  // STATS
  // =======================================================

  const stats =
    useMemo(() => {
      return visibleStockItems.reduce(
        (
          accumulator,
          item
        ) => {
          accumulator.quantity +=
            toNumber(
              item.quantity
            )

          accumulator.available +=
            toNumber(
              item.available_quantity
            )

          accumulator.purchaseValue +=
            toNumber(
              item.purchase_value
            )

          accumulator.sellingValue +=
            toNumber(
              item.selling_value
            )

          accumulator.margin +=
            toNumber(
              item.potential_margin
            )

          accumulator.purchaseValueCdf +=
            toNumber(
              item.purchase_value_cdf
            )

          accumulator.sellingValueCdf +=
            toNumber(
              item.selling_value_cdf
            )

          accumulator.marginCdf +=
            toNumber(
              item.potential_margin_cdf
            )

          return accumulator
        },
        {
          lines:
            visibleStockItems.length,

          quantity: 0,

          available: 0,

          purchaseValue: 0,

          sellingValue: 0,

          margin: 0,

          purchaseValueCdf: 0,

          sellingValueCdf: 0,

          marginCdf: 0,
        }
      )
    }, [
      visibleStockItems,
    ])


  // =======================================================
  // PRODUCTS AVAILABLE FOR STORE TRANSFER
  // =======================================================

  const productsForStoreTransfer =
    useMemo(() => {
      if (
        !transferStoreForm.source_warehouse_id
      ) {
        return products
      }

      const productIds =
        new Set(
          stockItems
            .filter(
              (item) =>
                String(
                  item.warehouse_id
                ) ===
                  String(
                    transferStoreForm.source_warehouse_id
                  ) &&
                toNumber(
                  item.available_quantity
                ) > 0
            )
            .map(
              (item) =>
                String(
                  item.product_id
                )
            )
        )

      return products.filter(
        (product) =>
          productIds.has(
            String(
              product.id
            )
          )
      )
    }, [
      products,
      stockItems,
      transferStoreForm.source_warehouse_id,
    ])


  // =======================================================
  // PRODUCTS AVAILABLE FOR WAREHOUSE TRANSFER
  // =======================================================

  const productsForWarehouseTransfer =
    useMemo(() => {
      if (
        !transferWarehouseForm.source_warehouse_id
      ) {
        return products
      }

      const productIds =
        new Set(
          stockItems
            .filter(
              (item) =>
                String(
                  item.warehouse_id
                ) ===
                  String(
                    transferWarehouseForm.source_warehouse_id
                  ) &&
                toNumber(
                  item.available_quantity
                ) > 0
            )
            .map(
              (item) =>
                String(
                  item.product_id
                )
            )
        )

      return products.filter(
        (product) =>
          productIds.has(
            String(
              product.id
            )
          )
      )
    }, [
      products,
      stockItems,
      transferWarehouseForm.source_warehouse_id,
    ])


  // =======================================================
  // CREATE WAREHOUSE
  // =======================================================

  const openCreateWarehouseDialog =
    () => {
      setEditingWarehouse(
        null
      )

      setWarehouseForm({
        ...emptyWarehouseForm,
      })

      setWarehouseDialogOpen(
        true
      )
    }


  // =======================================================
  // EDIT WAREHOUSE
  // =======================================================

  const openEditWarehouseDialog =
    (warehouse) => {
      setEditingWarehouse(
        warehouse
      )

      setWarehouseForm({
        code:
          warehouse.code || "",

        name:
          warehouse.name || "",

        warehouse_type:
          warehouse.warehouse_type ||
          "FINISHED_GOODS",

        address:
          warehouse.address || "",

        city:
          warehouse.city ||
          "Kinshasa",

        province:
          warehouse.province ||
          "Kinshasa",

        is_active:
          isActive(
            warehouse.is_active
          ),
      })

      setWarehouseDialogOpen(
        true
      )
    }


  // =======================================================
  // SAVE WAREHOUSE
  // =======================================================

  const saveWarehouse =
    async () => {
      if (
        !warehouseForm.name
          .trim()
      ) {
        showSnackbar(
          "Le nom de l'entrepôt est obligatoire.",
          "warning"
        )

        return
      }


      const payload = {
        name:
          warehouseForm.name.trim(),

        warehouse_type:
          warehouseForm.warehouse_type ||
          "FINISHED_GOODS",

        address:
          warehouseForm.address
            ?.trim() ||
          null,

        city:
          warehouseForm.city
            ?.trim() ||
          null,

        province:
          warehouseForm.province
            ?.trim() ||
          null,

        is_active:
          Boolean(
            warehouseForm.is_active
          ),
      }


      try {
        setSaving(true)

        if (
          editingWarehouse?.id
        ) {
          await updateWarehouse(
            editingWarehouse.id,
            payload
          )

          showSnackbar(
            "Entrepôt modifié avec succès."
          )
        } else {
          await createWarehouse(
            payload
          )

          showSnackbar(
            "Entrepôt créé avec succès."
          )
        }

        setWarehouseDialogOpen(
          false
        )

        setEditingWarehouse(
          null
        )

        await loadData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible d'enregistrer l'entrepôt."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DEACTIVATE WAREHOUSE
  // =======================================================

  const handleDeactivateWarehouse =
    async (warehouse) => {
      const confirmed =
        window.confirm(
          `Désactiver l'entrepôt ${warehouse.code} - ${warehouse.name} ?`
        )

      if (!confirmed) {
        return
      }

      try {
        await deactivateWarehouse(
          warehouse.id
        )

        showSnackbar(
          "Entrepôt désactivé."
        )

        if (
          String(
            selectedWarehouseId
          ) ===
          String(
            warehouse.id
          )
        ) {
          setSelectedWarehouseId(
            ""
          )
        }

        await loadData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de désactiver l'entrepôt."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // OPEN STOCK DIALOG
  // =======================================================

  const openStockDialog =
    (
      warehouseId = "",
      productId = ""
    ) => {
      setStockForm({
        ...emptyStockForm,

        warehouse_id:
          warehouseId ||
          selectedWarehouseId ||
          "",

        product_id:
          productId || "",

        unit_cost:
          productId
            ? getProductFactoryPrice(
                products,
                productId
              )
            : "",
      })

      setStockDialogOpen(
        true
      )
    }


  // =======================================================
  // SAVE STOCK
  // =======================================================

  const saveStockIn =
    async () => {
      if (
        !stockForm.warehouse_id
      ) {
        showSnackbar(
          "Choisissez l'entrepôt.",
          "warning"
        )

        return
      }

      if (
        !stockForm.product_id
      ) {
        showSnackbar(
          "Choisissez le produit.",
          "warning"
        )

        return
      }

      if (
        toNumber(
          stockForm.quantity
        ) <= 0
      ) {
        showSnackbar(
          "La quantité doit être positive.",
          "warning"
        )

        return
      }


      try {
        setSaving(true)

        await addWarehouseStock({
          warehouse_id:
            Number(
              stockForm.warehouse_id
            ),

          product_id:
            Number(
              stockForm.product_id
            ),

          quantity:
            Number(
              stockForm.quantity
            ),

          unit_cost:
            stockForm.unit_cost ===
            ""
              ? null
              : Number(
                  stockForm.unit_cost
                ),

          notes:
            stockForm.notes
              ?.trim() ||
            null,
        })


        showSnackbar(
          "Stock ajouté dans l'entrepôt."
        )

        setStockDialogOpen(
          false
        )

        setSelectedWarehouseId(
          String(
            stockForm.warehouse_id
          )
        )

        await loadData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible d'ajouter le stock."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // OPEN TRANSFER TO STORE
  // =======================================================

  const openTransferStoreDialog =
    (
      warehouseId = "",
      productId = ""
    ) => {
      setTransferStoreForm({
        ...emptyTransferStoreForm,

        source_warehouse_id:
          warehouseId ||
          selectedWarehouseId ||
          "",

        product_id:
          productId || "",

        unit_cost:
          productId
            ? getProductFactoryPrice(
                products,
                productId
              )
            : "",
      })

      setTransferStoreDialogOpen(
        true
      )
    }


  // =======================================================
  // SAVE TRANSFER TO STORE
  // =======================================================

  const saveTransferToStore =
    async () => {
      if (
        !transferStoreForm.source_warehouse_id
      ) {
        showSnackbar(
          "Choisissez l'entrepôt source.",
          "warning"
        )

        return
      }

      if (
        !transferStoreForm.destination_store_id
      ) {
        showSnackbar(
          "Choisissez le shop destination.",
          "warning"
        )

        return
      }

      if (
        !transferStoreForm.product_id
      ) {
        showSnackbar(
          "Choisissez le produit.",
          "warning"
        )

        return
      }

      if (
        toNumber(
          transferStoreForm.quantity
        ) <= 0
      ) {
        showSnackbar(
          "La quantité doit être positive.",
          "warning"
        )

        return
      }


      try {
        setSaving(true)

        await transferWarehouseToStore({
          source_warehouse_id:
            Number(
              transferStoreForm.source_warehouse_id
            ),

          destination_store_id:
            Number(
              transferStoreForm.destination_store_id
            ),

          product_id:
            Number(
              transferStoreForm.product_id
            ),

          quantity:
            Number(
              transferStoreForm.quantity
            ),

          unit_cost:
            transferStoreForm.unit_cost ===
            ""
              ? null
              : Number(
                  transferStoreForm.unit_cost
                ),

          notes:
            transferStoreForm.notes
              ?.trim() ||
            null,
        })


        showSnackbar(
          "Stock transféré vers le shop."
        )

        setTransferStoreDialogOpen(
          false
        )

        setSelectedWarehouseId(
          String(
            transferStoreForm.source_warehouse_id
          )
        )

        await loadData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de transférer vers le shop."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // OPEN WAREHOUSE TRANSFER
  // =======================================================

  const openTransferWarehouseDialog =
    (
      warehouseId = "",
      productId = ""
    ) => {
      setTransferWarehouseForm({
        ...emptyTransferWarehouseForm,

        source_warehouse_id:
          warehouseId ||
          selectedWarehouseId ||
          "",

        product_id:
          productId || "",

        unit_cost:
          productId
            ? getProductFactoryPrice(
                products,
                productId
              )
            : "",
      })

      setTransferWarehouseDialogOpen(
        true
      )
    }


  // =======================================================
  // SAVE WAREHOUSE TRANSFER
  // =======================================================

  const saveTransferToWarehouse =
    async () => {
      if (
        !transferWarehouseForm.source_warehouse_id
      ) {
        showSnackbar(
          "Choisissez l'entrepôt source.",
          "warning"
        )

        return
      }

      if (
        !transferWarehouseForm.destination_warehouse_id
      ) {
        showSnackbar(
          "Choisissez l'entrepôt destination.",
          "warning"
        )

        return
      }

      if (
        String(
          transferWarehouseForm.source_warehouse_id
        ) ===
        String(
          transferWarehouseForm.destination_warehouse_id
        )
      ) {
        showSnackbar(
          "Source et destination doivent être différentes.",
          "warning"
        )

        return
      }

      if (
        !transferWarehouseForm.product_id
      ) {
        showSnackbar(
          "Choisissez le produit.",
          "warning"
        )

        return
      }

      if (
        toNumber(
          transferWarehouseForm.quantity
        ) <= 0
      ) {
        showSnackbar(
          "La quantité doit être positive.",
          "warning"
        )

        return
      }


      try {
        setSaving(true)

        await transferWarehouseToWarehouse({
          source_warehouse_id:
            Number(
              transferWarehouseForm.source_warehouse_id
            ),

          destination_warehouse_id:
            Number(
              transferWarehouseForm.destination_warehouse_id
            ),

          product_id:
            Number(
              transferWarehouseForm.product_id
            ),

          quantity:
            Number(
              transferWarehouseForm.quantity
            ),

          unit_cost:
            transferWarehouseForm.unit_cost ===
            ""
              ? null
              : Number(
                  transferWarehouseForm.unit_cost
                ),

          notes:
            transferWarehouseForm.notes
              ?.trim() ||
            null,
        })


        showSnackbar(
          "Stock transféré vers l'autre entrepôt."
        )

        setTransferWarehouseDialogOpen(
          false
        )

        setSelectedWarehouseId(
          String(
            transferWarehouseForm.source_warehouse_id
          )
        )

        await loadData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de transférer vers l'entrepôt."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              lg:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="overline"

              sx={{
                color: "#2563eb",

                fontWeight: 900,

                letterSpacing: 1,

                lineHeight: 1,
              }}
            >
              STOCK & INVENTAIRE
            </Typography>


            <Typography
              variant="h4"

              sx={{
                mt: 0.45,

                color: "#0f172a",

                fontWeight: 900,

                letterSpacing:
                  "-0.025em",

                lineHeight: 1.1,

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Gestion des entrepôts
            </Typography>


            <Typography
              variant="body2"

              color="text.secondary"

              sx={{
                mt: 0.55,

                maxWidth: 800,

                lineHeight: 1.5,
              }}
            >
              Consultez les quantités disponibles,
              les valeurs de stock et les mouvements
              entre entrepôts et shops.
            </Typography>
          </Box>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                xl:
                  "repeat(4, auto)",
              },

              gap: 0.8,

              width: {
                xs: "100%",
                lg: "auto",
              },
            }}
          >
            <Button
              variant="contained"

              startIcon={
                <WarehouseIcon />
              }

              onClick={
                openCreateWarehouseDialog
              }
            >
              Nouvel entrepôt
            </Button>


            <Button
              variant="contained"

              color="success"

              startIcon={
                <AddIcon />
              }

              onClick={() =>
                openStockDialog()
              }
            >
              Ajouter du stock
            </Button>


            <Button
              variant="outlined"

              startIcon={
                <MoveDownIcon />
              }

              onClick={() =>
                openTransferStoreDialog()
              }
            >
              Transférer au shop
            </Button>


            <Button
              variant="outlined"

              color="secondary"

              startIcon={
                <InventoryIcon />
              }

              onClick={() =>
                openTransferWarehouseDialog()
              }
            >
              Transfert entrepôts
            </Button>
          </Box>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"

          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,
          }}
        >
          Chaque ligne correspond à un produit dans un
          entrepôt précis. Les entrepôts sont classés par
          usage : produits finis, matières premières,
          mixte ou transit. Les codes sont générés
          automatiquement.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              lg:
                "repeat(5, minmax(0, 1fr))",
            },

            gap: 1.05,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Références"

            value={
              stats.lines
            }

            caption="Produits en stock"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <InventoryIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Quantité"

            value={
              formatQuantity(
                stats.quantity
              )
            }

            caption={`Disponible : ${formatQuantity(
              stats.available
            )}`}

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <WarehouseIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Valeur achat"

            value={
              formatCurrency(
                stats.purchaseValue,
                "USD"
              )
            }

            caption={
              hasNumericValue(
                stats.purchaseValueCdf
              )
                ? formatCurrency(
                    stats.purchaseValueCdf,
                    "CDF"
                  )
                : "Valorisation stock"
            }

            accent="#d97706"

            background="rgba(217, 119, 6, 0.09)"

            icon={
              <AddIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Valeur vente"

            value={
              formatCurrency(
                stats.sellingValue,
                "USD"
              )
            }

            caption={
              hasNumericValue(
                stats.sellingValueCdf
              )
                ? formatCurrency(
                    stats.sellingValueCdf,
                    "CDF"
                  )
                : "Potentiel commercial"
            }

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <MoveDownIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Marge potentielle"

            value={
              formatCurrency(
                stats.margin,
                "USD"
              )
            }

            caption={
              hasNumericValue(
                stats.marginCdf
              )
                ? formatCurrency(
                    stats.marginCdf,
                    "CDF"
                  )
                : "Valeur estimée"
            }

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <VisibilityIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(250px, 1.3fr) minmax(280px, 1fr) auto",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              size="small"

              fullWidth

              label="Rechercher"

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }

              placeholder="Produit, code ou entrepôt..."
            />


            <TextField
              select

              size="small"

              label="Entrepôt"

              value={
                selectedWarehouseId
              }

              onChange={(
                event
              ) =>
                setSelectedWarehouseId(
                  event.target.value
                )
              }

              fullWidth
            >
              <MenuItem value="">
                Tous les entrepôts
              </MenuItem>

              {warehouses.map(
                (warehouse) => (
                  <MenuItem
                    key={
                      warehouse.id
                    }

                    value={
                      String(
                        warehouse.id
                      )
                    }
                  >
                    {getWarehouseLabel(
                      warehouse
                    )}
                  </MenuItem>
                )
              )}
            </TextField>


            <Button
              variant="outlined"

              onClick={
                loadData
              }

              disabled={
                loading
              }

              sx={{
                minWidth: {
                  xs: "100%",
                  lg: 130,
                },
              }}
            >
              {loading
                ? "Chargement..."
                : "Actualiser"}
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            STOCK REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            mb: 2.2,

            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",
          }}
        >

          {/* HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Stock par produit et entrepôt
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Quantités, réservations, prix et
                valorisation du stock
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${visibleStockItems.length} référence${
                visibleStockItems.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              STOCK DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <>
              <Box
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table
                  size="small"

                  sx={{
                    minWidth: 1050,

                    "& .MuiTableCell-head":
                      {
                        bgcolor: "#f8fafc",

                        color: "#334155",

                        fontWeight: 900,

                        whiteSpace:
                          "nowrap",
                      },

                    "& .MuiTableCell-body":
                      {
                        py: 1,

                        borderColor:
                          "rgba(148, 163, 184, 0.14)",
                      },

                    "& .MuiTableRow-root:hover":
                      {
                        bgcolor:
                          "#f8fbff",
                      },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Produit
                      </TableCell>

                      <TableCell>
                        Entrepôt
                      </TableCell>

                      <TableCell>
                        Stock
                      </TableCell>

                      <TableCell align="right">
                        Prix
                      </TableCell>

                      <TableCell align="right">
                        Valorisation
                      </TableCell>

                      <TableCell align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>


                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{
                            py: 5,
                          }}
                        >
                          Chargement...
                        </TableCell>
                      </TableRow>
                    ) : paginatedStockItems.length ===
                      0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{
                            py: 5,
                          }}
                        >
                          <Typography
                            color="text.secondary"
                          >
                            Aucun produit en stock dans les entrepôts.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedStockItems.map(
                        (row) => (
                          <TableRow
                            key={`${row.warehouse_id}-${row.product_id}-${row.stock_balance_id}`}
                            hover
                          >

                            {/* PRODUCT */}

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {row.product_code ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.product_name ||
                                  "-"}
                              </Typography>
                            </TableCell>


                            {/* WAREHOUSE */}

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={800}
                              >
                                {row.warehouse_code ||
                                  "-"}
                                {" - "}
                                {row.warehouse_name ||
                                  "-"}
                              </Typography>

                              <Chip
                                size="small"

                                variant="outlined"

                                color={getWarehouseTypeColor(
                                  row.warehouse_type
                                )}

                                label={getWarehouseTypeLabel(
                                  row.warehouse_type
                                )}

                                sx={{
                                  mt: 0.4,
                                }}
                              />
                            </TableCell>


                            {/* STOCK */}

                            <TableCell>
                              <Stack
                                spacing={0.15}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatQuantity(
                                    row.quantity
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Réservé :{" "}
                                  {formatQuantity(
                                    row.reserved_quantity
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color={
                                    toNumber(
                                      row.available_quantity
                                    ) > 0
                                      ? "success.main"
                                      : "error.main"
                                  }
                                  fontWeight={800}
                                >
                                  Disponible :{" "}
                                  {formatQuantity(
                                    row.available_quantity
                                  )}
                                </Typography>
                              </Stack>
                            </TableCell>


                            {/* PRICES */}

                            <TableCell align="right">
                              <Stack
                                spacing={0.6}
                                alignItems="flex-end"
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Achat
                                  </Typography>

                                  <PriceBlock
                                    usd={
                                      row.factory_price
                                    }
                                    cdf={
                                      row.display_factory_price_cdf ||
                                      row.factory_price_cdf
                                    }
                                  />
                                </Box>

                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Vente
                                  </Typography>

                                  <PriceBlock
                                    usd={
                                      row.selling_price
                                    }
                                    cdf={
                                      row.display_selling_price_cdf ||
                                      row.selling_price_cdf
                                    }
                                  />
                                </Box>
                              </Stack>
                            </TableCell>


                            {/* VALUES */}

                            <TableCell align="right">
                              <Stack
                                spacing={0.6}
                                alignItems="flex-end"
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Achat
                                  </Typography>

                                  <PriceBlock
                                    usd={
                                      row.purchase_value
                                    }
                                    cdf={
                                      row.purchase_value_cdf
                                    }
                                  />
                                </Box>

                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Vente
                                  </Typography>

                                  <PriceBlock
                                    usd={
                                      row.selling_value
                                    }
                                    cdf={
                                      row.selling_value_cdf
                                    }
                                  />
                                </Box>

                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="success.main"
                                    fontWeight={800}
                                  >
                                    Marge
                                  </Typography>

                                  <PriceBlock
                                    usd={
                                      row.potential_margin
                                    }
                                    cdf={
                                      row.potential_margin_cdf
                                    }
                                  />
                                </Box>
                              </Stack>
                            </TableCell>


                            {/* ACTIONS */}

                            <TableCell align="right">
                              <Stack
                                spacing={0.55}
                                alignItems="stretch"
                                sx={{
                                  minWidth: 112,
                                }}
                              >
                                <Button
                                  size="small"

                                  variant="contained"

                                  color="success"

                                  onClick={() =>
                                    openStockDialog(
                                      String(
                                        row.warehouse_id
                                      ),
                                      String(
                                        row.product_id
                                      )
                                    )
                                  }
                                >
                                  + Stock
                                </Button>


                                <Button
                                  size="small"

                                  variant="outlined"

                                  onClick={() =>
                                    openTransferStoreDialog(
                                      String(
                                        row.warehouse_id
                                      ),
                                      String(
                                        row.product_id
                                      )
                                    )
                                  }
                                >
                                  Vers shop
                                </Button>


                                <Button
                                  size="small"

                                  variant="outlined"

                                  color="secondary"

                                  onClick={() =>
                                    openTransferWarehouseDialog(
                                      String(
                                        row.warehouse_id
                                      ),
                                      String(
                                        row.product_id
                                      )
                                    )
                                  }
                                >
                                  Entrepôt
                                </Button>


                                <Button
                                  size="small"

                                  variant="outlined"

                                  startIcon={
                                    <VisibilityIcon />
                                  }

                                  onClick={() =>
                                    setSelectedWarehouseId(
                                      String(
                                        row.warehouse_id
                                      )
                                    )
                                  }
                                >
                                  Voir
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>


              <TablePagination
                component="div"

                count={
                  visibleStockItems.length
                }

                page={
                  stockPage
                }

                onPageChange={(
                  _,
                  newPage
                ) =>
                  setStockPage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  borderTop:
                    "1px solid",

                  borderColor:
                    "divider",

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </>
          ) : (

            /* =================================================
               STOCK MOBILE
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement du stock...
                  </Typography>
                </Box>
              ) : paginatedStockItems.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun produit en stock.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedStockItems.map(
                    (row) => (
                      <Paper
                        key={`${row.warehouse_id}-${row.product_id}-${row.stock_balance_id}`}

                        variant="outlined"

                        sx={{
                          p: 1.4,

                          borderRadius: 3,

                          bgcolor: "#ffffff",
                        }}
                      >
                        <Stack spacing={1.25}>

                          {/* PRODUCT */}

                          <Box
                            sx={{
                              display: "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {row.product_name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.product_code ||
                                  "-"}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"

                              variant="outlined"

                              color={getWarehouseTypeColor(
                                row.warehouse_type
                              )}

                              label={getWarehouseTypeLabel(
                                row.warehouse_type
                              )}
                            />
                          </Box>


                          {/* WAREHOUSE */}

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Entrepôt
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight={800}
                            >
                              {row.warehouse_code ||
                                "-"}
                              {" - "}
                              {row.warehouse_name ||
                                "-"}
                            </Typography>
                          </Box>


                          {/* QUANTITIES */}

                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs:
                                    "repeat(3, minmax(0, 1fr))",
                                },

                              gap: 0.7,
                            }}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 0.9,
                                borderRadius: 2.5,
                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Total
                              </Typography>

                              <Typography
                                fontWeight={900}
                              >
                                {formatQuantity(
                                  row.quantity
                                )}
                              </Typography>
                            </Paper>


                            <Paper
                              variant="outlined"
                              sx={{
                                p: 0.9,
                                borderRadius: 2.5,
                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Réservé
                              </Typography>

                              <Typography
                                fontWeight={900}
                              >
                                {formatQuantity(
                                  row.reserved_quantity
                                )}
                              </Typography>
                            </Paper>


                            <Paper
                              variant="outlined"
                              sx={{
                                p: 0.9,
                                borderRadius: 2.5,
                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Disponible
                              </Typography>

                              <Typography
                                fontWeight={900}
                                color={
                                  toNumber(
                                    row.available_quantity
                                  ) > 0
                                    ? "success.main"
                                    : "error.main"
                                }
                              >
                                {formatQuantity(
                                  row.available_quantity
                                )}
                              </Typography>
                            </Paper>
                          </Box>


                          {/* PRICES */}

                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 1,
                            }}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                borderRadius: 2.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Prix achat
                              </Typography>

                              <PriceBlock
                                align="left"

                                usd={
                                  row.factory_price
                                }

                                cdf={
                                  row.display_factory_price_cdf ||
                                  row.factory_price_cdf
                                }
                              />
                            </Paper>


                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                borderRadius: 2.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Prix vente
                              </Typography>

                              <PriceBlock
                                align="left"

                                usd={
                                  row.selling_price
                                }

                                cdf={
                                  row.display_selling_price_cdf ||
                                  row.selling_price_cdf
                                }
                              />
                            </Paper>
                          </Box>


                          {/* VALUE */}

                          <Paper
                            variant="outlined"

                            sx={{
                              p: 1,

                              borderRadius: 2.5,

                              bgcolor: "#f8fafc",
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",

                                gridTemplateColumns:
                                  "repeat(3, minmax(0, 1fr))",

                                gap: 0.8,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Valeur achat
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatCurrency(
                                    row.purchase_value,
                                    "USD"
                                  )}
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Valeur vente
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatCurrency(
                                    row.selling_value,
                                    "USD"
                                  )}
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Marge
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                  color="success.main"
                                >
                                  {formatCurrency(
                                    row.potential_margin,
                                    "USD"
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 0.7,
                            }}
                          >
                            <Button
                              size="small"

                              variant="contained"

                              color="success"

                              onClick={() =>
                                openStockDialog(
                                  String(
                                    row.warehouse_id
                                  ),
                                  String(
                                    row.product_id
                                  )
                                )
                              }
                            >
                              + Stock
                            </Button>


                            <Button
                              size="small"

                              variant="outlined"

                              onClick={() =>
                                openTransferStoreDialog(
                                  String(
                                    row.warehouse_id
                                  ),
                                  String(
                                    row.product_id
                                  )
                                )
                              }
                            >
                              Vers shop
                            </Button>


                            <Button
                              size="small"

                              variant="outlined"

                              color="secondary"

                              onClick={() =>
                                openTransferWarehouseDialog(
                                  String(
                                    row.warehouse_id
                                  ),
                                  String(
                                    row.product_id
                                  )
                                )
                              }
                            >
                              Autre entrepôt
                            </Button>


                            <Button
                              size="small"

                              variant="outlined"

                              startIcon={
                                <VisibilityIcon />
                              }

                              onClick={() =>
                                setSelectedWarehouseId(
                                  String(
                                    row.warehouse_id
                                  )
                                )
                              }
                            >
                              Voir entrepôt
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  visibleStockItems.length
                }

                page={
                  stockPage
                }

                onPageChange={(
                  _,
                  newPage
                ) =>
                  setStockPage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor: "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>


        {/* =================================================
            WAREHOUSES REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",
          }}
        >

          {/* HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Liste des entrepôts
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Répertoire, type d'usage,
                localisation et volume de stock
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredWarehouses.length} entrepôt${
                filteredWarehouses.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              WAREHOUSE DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <>
              <Box
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table
                  size="small"

                  sx={{
                    minWidth: 850,

                    "& .MuiTableCell-head":
                      {
                        bgcolor: "#f8fafc",

                        color: "#334155",

                        fontWeight: 900,

                        whiteSpace:
                          "nowrap",
                      },

                    "& .MuiTableCell-body":
                      {
                        py: 1,

                        borderColor:
                          "rgba(148, 163, 184, 0.14)",
                      },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Entrepôt
                      </TableCell>

                      <TableCell>
                        Type
                      </TableCell>

                      <TableCell>
                        Localisation
                      </TableCell>

                      <TableCell>
                        Stock
                      </TableCell>

                      <TableCell>
                        Statut
                      </TableCell>

                      <TableCell align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>


                  <TableBody>
                    {paginatedWarehouses.length ===
                    0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}

                          align="center"

                          sx={{
                            py: 5,
                          }}
                        >
                          <Typography
                            color="text.secondary"
                          >
                            Aucun entrepôt trouvé.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedWarehouses.map(
                        (warehouse) => (
                          <TableRow
                            key={
                              warehouse.id
                            }

                            hover
                          >

                            {/* WAREHOUSE */}

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {warehouse.code ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={700}
                              >
                                {warehouse.name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {warehouse.address ||
                                  "-"}
                              </Typography>
                            </TableCell>


                            {/* TYPE */}

                            <TableCell>
                              <Chip
                                size="small"

                                variant="outlined"

                                color={getWarehouseTypeColor(
                                  warehouse.warehouse_type
                                )}

                                label={getWarehouseTypeLabel(
                                  warehouse.warehouse_type
                                )}
                              />
                            </TableCell>


                            {/* LOCATION */}

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                              >
                                {warehouse.city ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {warehouse.province ||
                                  "-"}
                              </Typography>
                            </TableCell>


                            {/* STOCK */}

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={900}
                              >
                                {formatQuantity(
                                  warehouse.quantity
                                )}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatQuantity(
                                  warehouse.product_count
                                )}{" "}
                                produit(s)
                              </Typography>
                            </TableCell>


                            {/* STATUS */}

                            <TableCell>
                              <Chip
                                size="small"

                                color={
                                  isActive(
                                    warehouse.is_active
                                  )
                                    ? "success"
                                    : "default"
                                }

                                variant={
                                  isActive(
                                    warehouse.is_active
                                  )
                                    ? "filled"
                                    : "outlined"
                                }

                                label={
                                  isActive(
                                    warehouse.is_active
                                  )
                                    ? "Actif"
                                    : "Inactif"
                                }
                              />
                            </TableCell>


                            {/* ACTIONS */}

                            <TableCell align="right">
                              <Stack
                                direction="row"

                                spacing={0.6}

                                justifyContent="flex-end"
                              >
                                <Button
                                  size="small"

                                  variant="outlined"

                                  onClick={() =>
                                    setSelectedWarehouseId(
                                      String(
                                        warehouse.id
                                      )
                                    )
                                  }
                                >
                                  Voir stock
                                </Button>


                                <Button
                                  size="small"

                                  variant="contained"

                                  onClick={() =>
                                    openEditWarehouseDialog(
                                      warehouse
                                    )
                                  }
                                >
                                  Modifier
                                </Button>


                                {isActive(
                                  warehouse.is_active
                                ) && (
                                  <Button
                                    size="small"

                                    variant="outlined"

                                    color="error"

                                    onClick={() =>
                                      handleDeactivateWarehouse(
                                        warehouse
                                      )
                                    }
                                  >
                                    Désactiver
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>


              <TablePagination
                component="div"

                count={
                  filteredWarehouses.length
                }

                page={
                  warehousePage
                }

                onPageChange={(
                  _,
                  newPage
                ) =>
                  setWarehousePage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  borderTop:
                    "1px solid",

                  borderColor:
                    "divider",

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </>
          ) : (

            /* =================================================
               WAREHOUSE MOBILE
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {paginatedWarehouses.length ===
              0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun entrepôt trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedWarehouses.map(
                    (warehouse) => (
                      <Paper
                        key={
                          warehouse.id
                        }

                        variant="outlined"

                        sx={{
                          p: 1.4,

                          borderRadius: 3,

                          bgcolor: "#ffffff",
                        }}
                      >
                        <Stack spacing={1.2}>
                          <Box
                            sx={{
                              display: "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {warehouse.name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {warehouse.code ||
                                  "-"}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"

                              color={
                                isActive(
                                  warehouse.is_active
                                )
                                  ? "success"
                                  : "default"
                              }

                              variant={
                                isActive(
                                  warehouse.is_active
                                )
                                  ? "filled"
                                  : "outlined"
                              }

                              label={
                                isActive(
                                  warehouse.is_active
                                )
                                  ? "Actif"
                                  : "Inactif"
                              }
                            />
                          </Box>


                          <Chip
                            size="small"

                            variant="outlined"

                            color={getWarehouseTypeColor(
                              warehouse.warehouse_type
                            )}

                            label={getWarehouseTypeLabel(
                              warehouse.warehouse_type
                            )}

                            sx={{
                              alignSelf:
                                "flex-start",
                            }}
                          />


                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Localisation
                            </Typography>

                            <Typography
                              variant="body2"
                            >
                              {warehouse.city ||
                                "-"}
                              {" / "}
                              {warehouse.province ||
                                "-"}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {warehouse.address ||
                                "-"}
                            </Typography>
                          </Box>


                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                "repeat(2, minmax(0, 1fr))",

                              gap: 1,
                            }}
                          >
                            <Paper
                              variant="outlined"

                              sx={{
                                p: 1,

                                borderRadius: 2.5,

                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Produits
                              </Typography>

                              <Typography
                                fontWeight={900}
                              >
                                {formatQuantity(
                                  warehouse.product_count
                                )}
                              </Typography>
                            </Paper>


                            <Paper
                              variant="outlined"

                              sx={{
                                p: 1,

                                borderRadius: 2.5,

                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Quantité
                              </Typography>

                              <Typography
                                fontWeight={900}
                              >
                                {formatQuantity(
                                  warehouse.quantity
                                )}
                              </Typography>
                            </Paper>
                          </Box>


                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 0.7,
                            }}
                          >
                            <Button
                              size="small"

                              variant="outlined"

                              onClick={() =>
                                setSelectedWarehouseId(
                                  String(
                                    warehouse.id
                                  )
                                )
                              }
                            >
                              Voir stock
                            </Button>


                            <Button
                              size="small"

                              variant="contained"

                              onClick={() =>
                                openEditWarehouseDialog(
                                  warehouse
                                )
                              }
                            >
                              Modifier
                            </Button>


                            {isActive(
                              warehouse.is_active
                            ) && (
                              <Button
                                size="small"

                                variant="outlined"

                                color="error"

                                onClick={() =>
                                  handleDeactivateWarehouse(
                                    warehouse
                                  )
                                }

                                sx={{
                                  gridColumn: {
                                    xs: "auto",
                                    sm: "1 / -1",
                                  },
                                }}
                              >
                                Désactiver
                              </Button>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredWarehouses.length
                }

                page={
                  warehousePage
                }

                onPageChange={(
                  _,
                  newPage
                ) =>
                  setWarehousePage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor: "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>


        {/* =================================================
            WAREHOUSE DIALOG
        ================================================= */}

        <Dialog
          open={
            warehouseDialogOpen
          }

          onClose={() => {
            if (!saving) {
              setWarehouseDialogOpen(
                false
              )
            }
          }}

          fullWidth

          maxWidth="sm"

          fullScreen={
            isSmDown
          }

          PaperProps={{
            sx: {
              borderRadius: {
                xs: 0,
                sm: 4,
              },

              m: {
                xs: 0,
                sm: 2,
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              fontWeight={900}
              fontSize={20}
              color="#0f172a"
            >
              {editingWarehouse
                ? "Modifier l'entrepôt"
                : "Nouvel entrepôt"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.3,
              }}
            >
              {editingWarehouse
                ? "Modifiez l'identification, le type et la localisation de cet entrepôt."
                : "Ajoutez un nouvel emplacement de stockage à RLSys."}
            </Typography>
          </DialogTitle>


          <DialogContent dividers>
            <Stack
              spacing={2}
              sx={{
                pt: 0.5,
              }}
            >
              {editingWarehouse ? (
                <TextField
                  size="small"

                  label="Code"

                  value={
                    warehouseForm.code
                  }

                  fullWidth

                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}

                  helperText="Le code entrepôt ne se modifie pas après création."
                />
              ) : (
                <Alert
                  severity="info"
                  variant="outlined"
                >
                  Le code de l'entrepôt sera généré
                  automatiquement par RLSys. Exemple :
                  WH-001.
                </Alert>
              )}


              <TextField
                size="small"

                label="Nom"

                value={
                  warehouseForm.name
                }

                onChange={(
                  event
                ) =>
                  setWarehouseForm(
                    (previous) => ({
                      ...previous,

                      name:
                        event.target.value,
                    })
                  )
                }

                placeholder="Ex. Entrepôt central"

                fullWidth

                required

                disabled={
                  saving
                }
              />


              <TextField
                select

                size="small"

                label="Type d'entrepôt"

                value={
                  warehouseForm.warehouse_type
                }

                onChange={(
                  event
                ) =>
                  setWarehouseForm(
                    (previous) => ({
                      ...previous,

                      warehouse_type:
                        event.target.value,
                    })
                  )
                }

                helperText="Définit l'usage métier principal."

                fullWidth

                required

                disabled={
                  saving
                }
              >
                {WAREHOUSE_TYPES.map(
                  (type) => (
                    <MenuItem
                      key={
                        type.value
                      }

                      value={
                        type.value
                      }
                    >
                      {type.label}
                    </MenuItem>
                  )
                )}
              </TextField>


              <TextField
                size="small"

                label="Adresse"

                value={
                  warehouseForm.address
                }

                onChange={(
                  event
                ) =>
                  setWarehouseForm(
                    (previous) => ({
                      ...previous,

                      address:
                        event.target.value,
                    })
                  )
                }

                fullWidth

                disabled={
                  saving
                }
              />


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      sm:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Ville"

                  value={
                    warehouseForm.city
                  }

                  onChange={(
                    event
                  ) =>
                    setWarehouseForm(
                      (previous) => ({
                        ...previous,

                        city:
                          event.target.value,
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />


                <TextField
                  size="small"

                  label="Province"

                  value={
                    warehouseForm.province
                  }

                  onChange={(
                    event
                  ) =>
                    setWarehouseForm(
                      (previous) => ({
                        ...previous,

                        province:
                          event.target.value,
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              {editingWarehouse && (
                <TextField
                  select

                  size="small"

                  label="Statut"

                  value={
                    String(
                      warehouseForm.is_active
                    )
                  }

                  onChange={(
                    event
                  ) =>
                    setWarehouseForm(
                      (previous) => ({
                        ...previous,

                        is_active:
                          event.target.value ===
                          "true",
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="true">
                    Actif
                  </MenuItem>

                  <MenuItem value="false">
                    Inactif
                  </MenuItem>
                </TextField>
              )}
            </Stack>
          </DialogContent>


          <DialogActions
            sx={{
              px: {
                xs: 2,
                sm: 3,
              },

              py: 2,

              borderTop:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Button
              onClick={() =>
                setWarehouseDialogOpen(
                  false
                )
              }

              disabled={
                saving
              }
            >
              Annuler
            </Button>


            <Button
              variant="contained"

              onClick={
                saveWarehouse
              }

              disabled={
                saving
              }
            >
              {saving
                ? "Enregistrement..."
                : editingWarehouse
                ? "Modifier"
                : "Créer"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            ADD STOCK DIALOG
        ================================================= */}

        <Dialog
          open={
            stockDialogOpen
          }

          onClose={() => {
            if (!saving) {
              setStockDialogOpen(
                false
              )
            }
          }}

          fullWidth

          maxWidth="sm"

          fullScreen={
            isSmDown
          }

          PaperProps={{
            sx: {
              borderRadius: {
                xs: 0,
                sm: 4,
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              fontWeight={900}
              fontSize={20}
              color="#0f172a"
            >
              Ajouter du stock
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.3,
              }}
            >
              Enregistrez une entrée de stock dans un
              entrepôt.
            </Typography>
          </DialogTitle>


          <DialogContent dividers>
            <Stack
              spacing={2}
              sx={{
                pt: 0.5,
              }}
            >
              <TextField
                select

                size="small"

                label="Entrepôt"

                value={
                  stockForm.warehouse_id
                }

                onChange={(
                  event
                ) =>
                  setStockForm(
                    (previous) => ({
                      ...previous,

                      warehouse_id:
                        event.target.value,
                    })
                  )
                }

                fullWidth

                disabled={
                  saving
                }
              >
                {activeWarehouses.length ===
                0 ? (
                  <MenuItem
                    value=""
                    disabled
                  >
                    Aucun entrepôt actif
                  </MenuItem>
                ) : (
                  activeWarehouses.map(
                    (warehouse) => (
                      <MenuItem
                        key={
                          warehouse.id
                        }

                        value={
                          String(
                            warehouse.id
                          )
                        }
                      >
                        {getWarehouseLabel(
                          warehouse
                        )}
                      </MenuItem>
                    )
                  )
                )}
              </TextField>


              <Autocomplete
                options={
                  products
                }

                value={findProduct(
                  products,
                  stockForm.product_id
                )}

                onChange={(
                  _,
                  product
                ) => {
                  const productId =
                    product?.id
                      ? String(
                          product.id
                        )
                      : ""

                  setStockForm(
                    (previous) => ({
                      ...previous,

                      product_id:
                        productId,

                      unit_cost:
                        productId
                          ? getProductFactoryPrice(
                              products,
                              productId
                            )
                          : "",
                    })
                  )
                }}

                getOptionLabel={
                  getProductSearchLabel
                }

                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  String(
                    option.id
                  ) ===
                  String(
                    value.id
                  )
                }

                renderOption={
                  renderProductOption
                }

                noOptionsText="Aucun produit trouvé"

                disabled={
                  saving
                }

                fullWidth

                renderInput={(
                  params
                ) => (
                  <TextField
                    {...params}

                    size="small"

                    label="Produit"

                    placeholder="Code ou nom du produit..."
                  />
                )}
              />


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      sm:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Quantité"

                  type="number"

                  value={
                    stockForm.quantity
                  }

                  onChange={(
                    event
                  ) =>
                    setStockForm(
                      (previous) => ({
                        ...previous,

                        quantity:
                          event.target.value,
                      })
                    )
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}

                  fullWidth

                  disabled={
                    saving
                  }
                />


                <TextField
                  size="small"

                  label="Prix / coût unitaire"

                  type="number"

                  value={
                    stockForm.unit_cost
                  }

                  onChange={(
                    event
                  ) =>
                    setStockForm(
                      (previous) => ({
                        ...previous,

                        unit_cost:
                          event.target.value,
                      })
                    )
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              <TextField
                label="Notes"

                value={
                  stockForm.notes
                }

                onChange={(
                  event
                ) =>
                  setStockForm(
                    (previous) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }

                multiline

                minRows={3}

                fullWidth

                disabled={
                  saving
                }

                placeholder="Ex. entrée production usine, stock initial, correction inventaire..."
              />
            </Stack>
          </DialogContent>


          <DialogActions
            sx={{
              px: {
                xs: 2,
                sm: 3,
              },

              py: 2,

              borderTop:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Button
              onClick={() =>
                setStockDialogOpen(
                  false
                )
              }

              disabled={
                saving
              }
            >
              Annuler
            </Button>


            <Button
              variant="contained"

              onClick={
                saveStockIn
              }

              disabled={
                saving
              }
            >
              {saving
                ? "Enregistrement..."
                : "Ajouter stock"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            TRANSFER TO STORE DIALOG
        ================================================= */}

        <Dialog
          open={
            transferStoreDialogOpen
          }

          onClose={() => {
            if (!saving) {
              setTransferStoreDialogOpen(
                false
              )
            }
          }}

          fullWidth

          maxWidth="sm"

          fullScreen={
            isSmDown
          }

          PaperProps={{
            sx: {
              borderRadius: {
                xs: 0,
                sm: 4,
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              fontWeight={900}
              fontSize={20}
              color="#0f172a"
            >
              Transférer vers un shop
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.3,
              }}
            >
              Déplacez une quantité disponible d'un
              entrepôt vers un point de vente.
            </Typography>
          </DialogTitle>


          <DialogContent dividers>
            <Stack
              spacing={2}
              sx={{
                pt: 0.5,
              }}
            >
              <TextField
                select

                size="small"

                label="Entrepôt source"

                value={
                  transferStoreForm.source_warehouse_id
                }

                onChange={(
                  event
                ) =>
                  setTransferStoreForm(
                    (previous) => ({
                      ...previous,

                      source_warehouse_id:
                        event.target.value,

                      product_id: "",

                      unit_cost: "",
                    })
                  )
                }

                fullWidth

                disabled={
                  saving
                }
              >
                {activeWarehouses.map(
                  (warehouse) => (
                    <MenuItem
                      key={
                        warehouse.id
                      }

                      value={
                        String(
                          warehouse.id
                        )
                      }
                    >
                      {getWarehouseLabel(
                        warehouse
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>


              <Autocomplete
                options={
                  productsForStoreTransfer
                }

                value={findProduct(
                  productsForStoreTransfer,
                  transferStoreForm.product_id
                )}

                onChange={(
                  _,
                  product
                ) => {
                  const productId =
                    product?.id
                      ? String(
                          product.id
                        )
                      : ""

                  setTransferStoreForm(
                    (previous) => ({
                      ...previous,

                      product_id:
                        productId,

                      unit_cost:
                        productId
                          ? getProductFactoryPrice(
                              products,
                              productId
                            )
                          : "",
                    })
                  )
                }}

                getOptionLabel={
                  getProductSearchLabel
                }

                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  String(
                    option.id
                  ) ===
                  String(
                    value.id
                  )
                }

                renderOption={
                  renderProductOption
                }

                noOptionsText="Aucun produit disponible pour cet entrepôt"

                disabled={
                  saving
                }

                fullWidth

                renderInput={(
                  params
                ) => (
                  <TextField
                    {...params}

                    size="small"

                    label="Produit disponible"

                    placeholder="Code ou nom du produit..."
                  />
                )}
              />


              <TextField
                select

                size="small"

                label="Shop destination"

                value={
                  transferStoreForm.destination_store_id
                }

                onChange={(
                  event
                ) =>
                  setTransferStoreForm(
                    (previous) => ({
                      ...previous,

                      destination_store_id:
                        event.target.value,
                    })
                  )
                }

                fullWidth

                disabled={
                  saving
                }
              >
                {stores.length ===
                0 ? (
                  <MenuItem
                    value=""
                    disabled
                  >
                    Aucun shop chargé
                  </MenuItem>
                ) : (
                  stores.map(
                    (store) => (
                      <MenuItem
                        key={
                          store.id
                        }

                        value={
                          String(
                            store.id
                          )
                        }
                      >
                        {getStoreLabel(
                          store
                        )}
                      </MenuItem>
                    )
                  )
                )}
              </TextField>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      sm:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Quantité"

                  type="number"

                  value={
                    transferStoreForm.quantity
                  }

                  onChange={(
                    event
                  ) =>
                    setTransferStoreForm(
                      (previous) => ({
                        ...previous,

                        quantity:
                          event.target.value,
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />


                <TextField
                  size="small"

                  label="Coût unitaire"

                  type="number"

                  value={
                    transferStoreForm.unit_cost
                  }

                  onChange={(
                    event
                  ) =>
                    setTransferStoreForm(
                      (previous) => ({
                        ...previous,

                        unit_cost:
                          event.target.value,
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              <TextField
                label="Notes"

                value={
                  transferStoreForm.notes
                }

                onChange={(
                  event
                ) =>
                  setTransferStoreForm(
                    (previous) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }

                multiline

                minRows={3}

                fullWidth

                disabled={
                  saving
                }
              />
            </Stack>
          </DialogContent>


          <DialogActions
            sx={{
              px: {
                xs: 2,
                sm: 3,
              },

              py: 2,

              borderTop:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Button
              onClick={() =>
                setTransferStoreDialogOpen(
                  false
                )
              }

              disabled={
                saving
              }
            >
              Annuler
            </Button>


            <Button
              variant="contained"

              onClick={
                saveTransferToStore
              }

              disabled={
                saving
              }
            >
              {saving
                ? "Transfert..."
                : "Transférer vers shop"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            WAREHOUSE TO WAREHOUSE DIALOG
        ================================================= */}

        <Dialog
          open={
            transferWarehouseDialogOpen
          }

          onClose={() => {
            if (!saving) {
              setTransferWarehouseDialogOpen(
                false
              )
            }
          }}

          fullWidth

          maxWidth="sm"

          fullScreen={
            isSmDown
          }

          PaperProps={{
            sx: {
              borderRadius: {
                xs: 0,
                sm: 4,
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              fontWeight={900}
              fontSize={20}
              color="#0f172a"
            >
              Transfert entre entrepôts
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.3,
              }}
            >
              Déplacez du stock disponible d'un
              entrepôt vers un autre.
            </Typography>
          </DialogTitle>


          <DialogContent dividers>
            <Stack
              spacing={2}
              sx={{
                pt: 0.5,
              }}
            >
              <TextField
                select

                size="small"

                label="Entrepôt source"

                value={
                  transferWarehouseForm.source_warehouse_id
                }

                onChange={(
                  event
                ) =>
                  setTransferWarehouseForm(
                    (previous) => ({
                      ...previous,

                      source_warehouse_id:
                        event.target.value,

                      destination_warehouse_id:
                        String(
                          previous.destination_warehouse_id
                        ) ===
                        String(
                          event.target.value
                        )
                          ? ""
                          : previous.destination_warehouse_id,

                      product_id: "",

                      unit_cost: "",
                    })
                  )
                }

                fullWidth

                disabled={
                  saving
                }
              >
                {activeWarehouses.map(
                  (warehouse) => (
                    <MenuItem
                      key={
                        warehouse.id
                      }

                      value={
                        String(
                          warehouse.id
                        )
                      }
                    >
                      {getWarehouseLabel(
                        warehouse
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>


              <Autocomplete
                options={
                  productsForWarehouseTransfer
                }

                value={findProduct(
                  productsForWarehouseTransfer,
                  transferWarehouseForm.product_id
                )}

                onChange={(
                  _,
                  product
                ) => {
                  const productId =
                    product?.id
                      ? String(
                          product.id
                        )
                      : ""

                  setTransferWarehouseForm(
                    (previous) => ({
                      ...previous,

                      product_id:
                        productId,

                      unit_cost:
                        productId
                          ? getProductFactoryPrice(
                              products,
                              productId
                            )
                          : "",
                    })
                  )
                }}

                getOptionLabel={
                  getProductSearchLabel
                }

                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  String(
                    option.id
                  ) ===
                  String(
                    value.id
                  )
                }

                renderOption={
                  renderProductOption
                }

                noOptionsText="Aucun produit disponible pour cet entrepôt"

                disabled={
                  saving
                }

                fullWidth

                renderInput={(
                  params
                ) => (
                  <TextField
                    {...params}

                    size="small"

                    label="Produit disponible"

                    placeholder="Code ou nom du produit..."
                  />
                )}
              />


              <TextField
                select

                size="small"

                label="Entrepôt destination"

                value={
                  transferWarehouseForm.destination_warehouse_id
                }

                onChange={(
                  event
                ) =>
                  setTransferWarehouseForm(
                    (previous) => ({
                      ...previous,

                      destination_warehouse_id:
                        event.target.value,
                    })
                  )
                }

                fullWidth

                disabled={
                  saving
                }
              >
                {activeWarehouses
                  .filter(
                    (warehouse) =>
                      String(
                        warehouse.id
                      ) !==
                      String(
                        transferWarehouseForm.source_warehouse_id
                      )
                  )
                  .map(
                    (warehouse) => (
                      <MenuItem
                        key={
                          warehouse.id
                        }

                        value={
                          String(
                            warehouse.id
                          )
                        }
                      >
                        {getWarehouseLabel(
                          warehouse
                        )}
                      </MenuItem>
                    )
                  )}
              </TextField>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      sm:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Quantité"

                  type="number"

                  value={
                    transferWarehouseForm.quantity
                  }

                  onChange={(
                    event
                  ) =>
                    setTransferWarehouseForm(
                      (previous) => ({
                        ...previous,

                        quantity:
                          event.target.value,
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />


                <TextField
                  size="small"

                  label="Coût unitaire"

                  type="number"

                  value={
                    transferWarehouseForm.unit_cost
                  }

                  onChange={(
                    event
                  ) =>
                    setTransferWarehouseForm(
                      (previous) => ({
                        ...previous,

                        unit_cost:
                          event.target.value,
                      })
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              <TextField
                label="Notes"

                value={
                  transferWarehouseForm.notes
                }

                onChange={(
                  event
                ) =>
                  setTransferWarehouseForm(
                    (previous) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }

                multiline

                minRows={3}

                fullWidth

                disabled={
                  saving
                }
              />
            </Stack>
          </DialogContent>


          <DialogActions
            sx={{
              px: {
                xs: 2,
                sm: 3,
              },

              py: 2,

              borderTop:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Button
              onClick={() =>
                setTransferWarehouseDialogOpen(
                  false
                )
              }

              disabled={
                saving
              }
            >
              Annuler
            </Button>


            <Button
              variant="contained"

              color="secondary"

              onClick={
                saveTransferToWarehouse
              }

              disabled={
                saving
              }
            >
              {saving
                ? "Transfert..."
                : "Transférer entrepôt"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            SNACKBAR
        ================================================= */}

        <Snackbar
          open={
            snackbar.open
          }

          autoHideDuration={
            3500
          }

          onClose={
            closeSnackbar
          }

          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Alert
            severity={
              snackbar.severity
            }

            onClose={
              closeSnackbar
            }

            variant="filled"

            sx={{
              width: "100%",
              borderRadius: 3,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}